/**
 * LLM API 配置页面
 *
 * 本页面用于管理大语言模型（LLM）的接入配置，支持多配置切换。
 * 系统在客户线索评分、NLP 分析等场景中会调用配置的 LLM 接口。
 *
 * 功能模块：
 *   1. 配置列表：展示所有 LLM 配置，支持启用/禁用/删除
 *   2. 新增/编辑：弹窗表单配置服务商、模型、API 地址和密钥
 *   3. 测试工具：通用测试（chat）和客户评分测试（scoring），支持从列表快捷带入配置
 */
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Button,
  Toast,
  Spin,
  Table,
  Tag,
  Modal,
  Popconfirm,
  Space,
  Typography,
} from "@douyinfe/semi-ui";
import { IconPlus, IconDelete, IconEdit, IconSearch } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();
const { Text } = Typography;

/** 提供商选项列表 */
const PROVIDER_OPTIONS = [
  { value: "qwen", label: "通义千问 (Qwen)" },
  { value: "glm", label: "智谱 GLM" },
  { value: "deepseek", label: "DeepSeek" },
  { value: "xiaomi", label: "小米 MiMo" },
  { value: "volcengine", label: "火山引擎豆包" },
  { value: "agnes", label: "Agnes AI" },
];

/**
 * 获取模型名称输入框的占位提示文本。
 * 火山方舟需要使用 Endpoint ID（ep-xxx），其他平台直接用模型名。
 */
const getModelPlaceholder = (provider: string): string => {
  if (provider === "volcengine") {
    return "请输入 Endpoint ID，如 ep-20240612090709-hzjz5";
  }
  return "留空使用默认模型";
};

/**
 * 获取模型名称输入框下方的辅助说明。
 * 火山方舟需要特殊提示。
 */
const getModelHelpText = (provider: string): string | null => {
  if (provider === "volcengine") {
    return "火山方舟需在控制台创建推理接入点，将 Endpoint ID（ep-xxx）填入此处";
  }
  return null;
};

/** LLM 配置接口 */
interface LlmConfigItem {
  id?: number;
  name?: string;
  status?: number;
  provider: string;
  apiKey: string;
  apiUrl: string;
  modelName: string;
  batchProvider?: string;
  temperature?: number;
  maxTokens?: number;
  maxRetries?: number;
}

/** 编辑表单初始值（新增/编辑共用） */
const EMPTY_FORM: LlmConfigItem = {
  name: "",
  provider: "qwen",
  modelName: "",
  apiUrl: "",
  apiKey: "",
  batchProvider: "",
  temperature: 0.1,
  maxTokens: 4096,
  maxRetries: 2,
  status: 1,
};

export default function LlmConfigPage() {
  // ====================== 配置列表状态 ======================
  const [loading, setLoading] = useState(false);
  const [configList, setConfigList] = useState<LlmConfigItem[]>([]);

  // ====================== 编辑弹窗状态 ======================
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LlmConfigItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [formApi, setFormApi] = useState<any>();
  /** 表单初始值，通过 initValues 驱动 Form 渲染，替代不可靠的 setTimeout+setValues */
  const [formInitValues, setFormInitValues] = useState<LlmConfigItem>(EMPTY_FORM);
  /** 当前选中的提供商（用于动态切换模型名称占位提示） */
  const [selectedProvider, setSelectedProvider] = useState<string>("qwen");

  // ====================== 测试状态 ======================
  const [testing, setTesting] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testFormApi, setTestFormApi] = useState<any>();
  const [testResult, setTestResult] = useState<string>("");
  const [testType, setTestType] = useState<"chat" | "scoring">("chat");
  /** 测试表单初始值，通过 initValues 驱动 Form 渲染 */
  const [testFormInitValues, setTestFormInitValues] = useState<any>({});
  /** 测试弹窗中当前选中的提供商 */
  const [testSelectedProvider, setTestSelectedProvider] = useState<string>("qwen");

  /** 组件挂载时加载配置列表 */
  useEffect(() => {
    loadConfigList();
  }, []);

  /**
   * 加载配置列表
   */
  const loadConfigList = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request<LlmConfigItem[]>("/llmConfig/list", "GET");
      setConfigList(resp.data || []);
    } catch (e) {
      console.error(e);
      Toast.error("加载配置列表失败");
    } finally {
      setLoading(false);
    }
  };

  /**
   * 打开新增弹窗
   */
  const handleAdd = () => {
    setEditingConfig(null);
    setFormInitValues({ ...EMPTY_FORM });
    setSelectedProvider("qwen");
    setModalVisible(true);
  };

  /**
   * 打开编辑弹窗，通过 initValues 直接回填已有数据
   */
  const handleEdit = (record: LlmConfigItem) => {
    setEditingConfig(record);
    setFormInitValues({ ...record });
    setSelectedProvider(record.provider || "qwen");
    setModalVisible(true);
  };

  /**
   * 保存配置（新增/编辑）
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      const values = formApi!.getValues();
      await apiClient.request("/llmConfig", "POST", undefined, values);
      Toast.success(editingConfig ? "更新成功" : "新增成功");
      setModalVisible(false);
      loadConfigList();
    } catch (e: any) {
      Toast.error(e?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  /**
   * 删除配置
   */
  const handleDelete = async (id: number) => {
    try {
      await apiClient.request(`/llmConfig/${id}`, "DELETE");
      Toast.success("删除成功");
      loadConfigList();
    } catch (e: any) {
      Toast.error(e?.message || "删除失败");
    }
  };

  /**
   * 打开测试弹窗
   * @param type 测试类型：chat=通用测试，scoring=客户评分测试
   * @param record 可选，从列表行点击时传入，自动带出配置参数
   */
  const handleOpenTest = (type: "chat" | "scoring", record?: LlmConfigItem) => {
    setTestType(type);
    setTestResult("");
    const provider = record?.provider || "qwen";
    setTestSelectedProvider(provider);
    // 通过 initValues 驱动表单渲染，确保配置参数可靠地带入
    setTestFormInitValues({
      provider,
      modelName: record?.modelName || "",
      apiUrl: record?.apiUrl || "",
      apiKey: record?.apiKey || "",
      systemPrompt: "",
      userInput: "",
      leadId: "",
    });
    setTestModalVisible(true);
  };

  /**
   * 执行测试
   */
  const handleTest = async () => {
    setTesting(true);
    setTestResult("");
    try {
      const values = testFormApi!.getValues();
      const config = {
        provider: values.provider,
        modelName: values.modelName,
        apiUrl: values.apiUrl,
        apiKey: values.apiKey,
      };

      let resp;
      if (testType === "chat") {
        resp = await apiClient.request<string>("/llmConfig/testChat", "POST", undefined, {
          config,
          systemPrompt: values.systemPrompt || "",
          userInput: values.userInput,
        });
      } else {
        resp = await apiClient.request<string>("/llmConfig/testLeadScoring", "POST", undefined, {
          config,
          userInput: values.leadId,
        });
      }
      setTestResult(resp.data || "无响应");
    } catch (e: any) {
      setTestResult("错误: " + (e?.message || "调用失败"));
    } finally {
      setTesting(false);
    }
  };

  /**
   * 格式化 JSON 显示
   */
  const formatJson = (jsonStr: string) => {
    try {
      const obj = JSON.parse(jsonStr);
      return JSON.stringify(obj, null, 2);
    } catch {
      return jsonStr;
    }
  };

  /** 表格列定义 */
  const columns = [
    {
      title: "配置名称",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text: string) => text || "-",
    },
    {
      title: "服务商",
      dataIndex: "provider",
      key: "provider",
      width: 140,
      render: (provider: string) => {
        const option = PROVIDER_OPTIONS.find((o) => o.value === provider);
        return option?.label || provider;
      },
    },
    {
      title: "模型",
      dataIndex: "modelName",
      key: "modelName",
      width: 160,
      render: (text: string) => text || "默认",
    },
    {
      title: "API 地址",
      dataIndex: "apiUrl",
      key: "apiUrl",
      width: 250,
      render: (text: string) => (
        <Text
          ellipsis={{ showTooltip: true }}
          style={{ maxWidth: 230 }}
        >
          {text || "默认"}
        </Text>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? "green" : "grey"}>
          {status === 1 ? "启用" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 300,
      render: (_: any, record: LlmConfigItem) => (
        <Space>
          <Button
            size="small"
            icon={<IconEdit />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={<IconSearch />}
            onClick={() => handleOpenTest("chat", record)}
          >
            通用测试
          </Button>
          <Button
            size="small"
            icon={<IconSearch />}
            onClick={() => handleOpenTest("scoring", record)}
          >
            评分测试
          </Button>
          <Popconfirm
            title="确定删除此配置？"
            onConfirm={() => record.id && handleDelete(record.id)}
          >
            <Button
              size="small"
              type="danger"
              icon={<IconDelete />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* ====================== 配置列表 ====================== */}
      <Card
        title="LLM API 配置"
        headerExtraContent={
          <Space>
            <Button
              theme="solid"
              icon={<IconPlus />}
              onClick={handleAdd}
            >
              新增配置
            </Button>
            <Button
              icon={<IconSearch />}
              onClick={() => handleOpenTest("chat")}
            >
              通用测试
            </Button>
            <Button
              icon={<IconSearch />}
              onClick={() => handleOpenTest("scoring")}
            >
              评分测试
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={configList}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Spin>
      </Card>

      {/* ====================== 新增/编辑弹窗 ====================== */}
      <Modal
        title={editingConfig ? "编辑配置" : "新增配置"}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setModalVisible(false)}>取消</Button>
            <Button theme="solid" loading={saving} onClick={handleSave}>
              保存
            </Button>
          </Space>
        }
        width={520}
      >
        <Form
          key={editingConfig?.id ?? "add"}
          getFormApi={(api: any) => setFormApi(api)}
          initValues={formInitValues}
          labelPosition="left"
        >
          <Form.Input
            field="name"
            label="配置名称"
            placeholder="如：生产环境Qwen、测试用DeepSeek"
            rules={[{ required: true, message: "请输入配置名称" }]}
          />
          <Form.Select
            field="provider"
            label="服务商"
            optionList={PROVIDER_OPTIONS}
            rules={[{ required: true, message: "请选择服务商" }]}
            onChange={(value: string) => setSelectedProvider(value)}
          />
          <Form.Input
            field="modelName"
            label="模型名称"
            placeholder={getModelPlaceholder(selectedProvider)}
            extraText={getModelHelpText(selectedProvider) || undefined}
          />
          <Form.Input
            field="apiUrl"
            label="API 地址"
            placeholder="留空使用默认地址"
          />
          <Form.Input
            field="apiKey"
            label="API Key"
            mode="password"
            placeholder="请输入 API Key"
            rules={[{ required: true, message: "请输入 API Key" }]}
          />
          <Form.Input
            field="batchProvider"
            label="批量评分提供商"
            placeholder="可选，如 xiaomi"
          />
          <Form.InputNumber
            field="temperature"
            label="Temperature"
            placeholder="0.1"
            min={0}
            max={2}
            step={0.1}
          />
          <Form.InputNumber
            field="maxTokens"
            label="最大 Token 数"
            placeholder="4096"
            min={1}
            max={128000}
          />
          <Form.InputNumber
            field="maxRetries"
            label="最大重试次数"
            placeholder="2"
            min={0}
            max={5}
          />
          <Form.Select
            field="status"
            label="状态"
            optionList={[
              { value: 1, label: "启用" },
              { value: 0, label: "禁用" },
            ]}
          />
        </Form>
      </Modal>

      {/* ====================== 测试弹窗 ====================== */}
      <Modal
        title={testType === "chat" ? "通用测试工具" : "客户评分测试"}
        visible={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          key={testType + JSON.stringify(testFormInitValues)}
          getFormApi={(api: any) => setTestFormApi(api)}
          initValues={testFormInitValues}
          labelPosition="left"
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Select
              field="provider"
              label="服务商"
              optionList={PROVIDER_OPTIONS}
              onChange={(value: string) => setTestSelectedProvider(value)}
            />
            <Form.Input
              field="modelName"
              label="模型名称"
              placeholder={getModelPlaceholder(testSelectedProvider)}
              extraText={getModelHelpText(testSelectedProvider) || undefined}
            />
          </div>
          <Form.Input
            field="apiUrl"
            label="API 地址"
            placeholder="留空使用默认地址"
          />
          <Form.Input
            field="apiKey"
            label="API Key"
            mode="password"
            placeholder="请输入 API Key"
          />

          {testType === "chat" ? (
            <>
              <Form.TextArea
                field="systemPrompt"
                label="系统提示词"
                placeholder="可选，设置 AI 的角色和行为规则"
                rows={3}
              />
              <Form.TextArea
                field="userInput"
                label="用户输入"
                placeholder="请输入要测试的内容"
                rows={4}
                rules={[{ required: true, message: "请输入测试内容" }]}
              />
            </>
          ) : (
            <Form.Input
              field="leadId"
              label="客户 ID"
              placeholder="请输入客户 ID（数字）"
              rules={[{ required: true, message: "请输入客户 ID" }]}
            />
          )}

          <div style={{ marginTop: 16, marginBottom: 16  }}>
            <Button
              theme="solid"
              loading={testing}
              onClick={handleTest}
            >
              {testType === "chat" ? "发送测试" : "测试评分"}
            </Button>
          </div>
        </Form>

        {/* 测试结果展示 */}
        {testResult && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>测试结果：</div>
            <pre
              style={{
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 400,
                overflow: "auto",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {testType === "scoring" ? formatJson(testResult) : testResult}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  );
}
